import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { formatBillPaymentDetails, formatPriceStr } from '../../util/billFormatting'
import { FlexColumn, FlexRow, WhiteBGContainer, FlexEndContainer, Divider, CenteredRowContainer } from '../styles/Containers'
import { ExpandableRowContainer, ActiveRow, InactiveRow, ExpandCollapseIconWrapper, ExpandedContentWrapper } from '../styles/ExpandableRowStyles'
import { BoldBodyText, BoldFormTitle, BoldMedBodyText, MedBodyText } from '../styles/Text'
import { billTypeMap, paymentMethodMap } from '../../util/billingConsts'
import SentBillRowMenu from './SentBillRowMenu'
import DetailSection from '../common/DetailSection'
import Bill from '../bill/Bill'
import BillStatus from './BillStatus'

const Item = styled.div`
  flex-basis: 16%;
`

const LastItem = styled(FlexEndContainer)`
  min-width: 120px;
`

const MenuContainer = styled(CenteredRowContainer)`
  height: ${props => `${props.height}px`};
`

function SentBillRow({ bill, requestorEmail = '', onBillUpdate = () => {} }) {
  const containerRef = useRef(null)

  const [showDetails, setShowDetails] = useState(false)
  const [height, setHeight] = useState(0)

  const toggleShowDetails = () => setShowDetails(showDetails => !showDetails)

  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const el = entry.target
        setHeight(el.clientHeight)
      }
    })
    resizeObserver.observe(containerRef.current)

    return () => resizeObserver.disconnect()
  }, [showDetails])

  const statusChangedDate =
    bill.status === 'WAITING' ? undefined : bill.status === 'PAID' ? new Date(bill.paymentDate) : new Date(bill.feeLastModified)

  const RowDetailsComponent = showDetails ? ActiveRow : InactiveRow

  return (
    <FlexRow>
      <ExpandableRowContainer active={showDetails}>
        <RowDetailsComponent ref={containerRef} onClick={toggleShowDetails} active={showDetails}>
          <Item>
            <BoldBodyText>{bill.secondaryId}</BoldBodyText>
          </Item>
          <Item>
            <MedBodyText>{billTypeMap[bill.type]}</MedBodyText>
          </Item>
          <Item>
            <MedBodyText>{formatPriceStr(bill.total)}</MedBodyText>
          </Item>
          <Item>
            <BoldMedBodyText>{format(bill.sentAt, 'MMM-dd-yyyy')}</BoldMedBodyText>
            <MedBodyText>{format(bill.sentAt, 'hh:mm a')}</MedBodyText>
          </Item>
          <Item>
            {statusChangedDate && (
              <>
                <BoldMedBodyText>{format(statusChangedDate, 'MMM-dd-yyyy')}</BoldMedBodyText>
                <MedBodyText>{format(statusChangedDate, 'hh:mm a')}</MedBodyText>
              </>
            )}
          </Item>
          <Item>{bill.paymentMethod && <MedBodyText>{paymentMethodMap[bill.paymentMethod]}</MedBodyText>}</Item>
          <LastItem>
            <BillStatus status={bill.status} />
          </LastItem>
          <ExpandCollapseIconWrapper>{showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ExpandCollapseIconWrapper>
        </RowDetailsComponent>
        {showDetails && (
          <ExpandedContentWrapper>
            <Divider />
            <FlexColumn gap="30px">
              {bill.status !== 'WAITING' && (
                <FlexColumn>
                  <BoldFormTitle>Payment Details</BoldFormTitle>
                  <WhiteBGContainer padding="30px">
                    <DetailSection details={formatBillPaymentDetails(bill)} />
                  </WhiteBGContainer>
                </FlexColumn>
              )}
              <Bill bill={bill} showCommunication />
            </FlexColumn>
          </ExpandedContentWrapper>
        )}
      </ExpandableRowContainer>
      <MenuContainer height={height}>
        <SentBillRowMenu bill={bill} handleBillUpdate={onBillUpdate} requestorEmail={requestorEmail} />
      </MenuContainer>
    </FlexRow>
  )
}

SentBillRow.propTypes = {
  bill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    secondaryId: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['WAITING', 'PAID', 'CANCELLED', 'REFUNDED', 'ABANDONED', 'REJECTED']),
    type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
    percentDue: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    amountDue: PropTypes.number.isRequired,
    lineItems: PropTypes.array.isRequired,
    sentAt: PropTypes.instanceOf(Date).isRequired,
    paymentDate: PropTypes.instanceOf(Date),
    paymentMethod: PropTypes.oneOf(['ONLINE', 'IN_PERSON']),
    paymentNote: PropTypes.string,
    feeLastModified: PropTypes.instanceOf(Date),
    paymentDueDate: PropTypes.instanceOf(Date)
  }).isRequired,
  requestorEmail: PropTypes.string,
  onBillUpdate: PropTypes.func
}

export default SentBillRow
